_collow.components.CoursesaiOverviewPage = class CoursesaiOverviewPage extends _collow.components.Base {
  // Class properties with descriptive comments
  root;               // Root DOM element
  elements;           // DOM elements collection
  params;             // Configuration parameters
  allCourses = [];    // All courses data
  coursesFiltered = []; // Filtered courses based on user selection
  coursesTypes = [];  // Course types for filtering
  coursesStatus = []; // Course statuses for filtering
  courseStatusFilters = []; // Selected status filters
  courseTypesFilters = []; // Selected type filters

  constructor(root, elements, params) {
    super();

    this.root = root;
    this.elements = elements;
    this.params = params;
    this.initPage()
  }

  // INITIALIZATION METHODS

  /**
   * Initialize the page by loading courses and setting up UI
   */
  initPage = async () => {
    await this.getCourses().then(() => {
      this.getCoursesStatusToFilter()
      this.getCoursesTypesToFilter()
      this.addTypesToFilterList()
      this.addEventListener()
    })
  }

  /**
   * Check if AI course generation feature is enabled
   * @returns {boolean} Whether AI course generation is enabled
   */
  checkHasAICourseGeneration = () => {
    const features = localStorage.getItem("whitelabelFeatures")
    return features ? JSON.parse(features).has_ai_course_generation : false
  }

  // DATA PROCESSING METHODS

  /**
   * Filter results to handle drafts and published items
   * @param {Array} results - Raw course data
   * @returns {Array} Filtered course list
   */
  filterGroqResults(results) {
    if (!results || !Array.isArray(results)) return [];

    // Filter out test and concept courses
    results = results.filter(item => {
      return item && item.status && item.status !== "concept";
    });

    const published = new Map();
    const drafts = new Map();

    // Separate drafts and published items
    results.forEach(item => {
      if (!item || !item.sanity_id) return;

      if (item.sanity_id.startsWith("drafts.")) {
        drafts.set(item.sanity_id.slice(7), item);
      } else {
        published.set(item.sanity_id, item);
      }
    });

    const filtered = [];

    // Add published items
    published.forEach(item => filtered.push(item));

    // Add drafts that don't have a published counterpart
    drafts.forEach((draft, key) => {
      if (!published.has(key)) {
        filtered.push(draft);
      }
    });

    return filtered;
  }

  /**
   * Fetch and process courses data
   */
  getCourses = async () => {
    try {
      const response = await fetching(this.params.path + "/admin/courses?type=all")
      const filteredCourses = this.filterGroqResults(response)
      this.allCourses = this.editCoursesKeys(filteredCourses)
      this.addCoursesToBody(this.allCourses)
    } catch (error) {
      console.error("Error fetching courses:", error);
      this.allCourses = [];
      this.addCoursesToBody([]);
    }
  }

  /**
   * Process course data to add required keys and format dates
   * @param {Array} response - Raw course data
   * @returns {Array} Processed course data
   */
  editCoursesKeys = (response) => {
    if (!response || !Array.isArray(response)) return [];

    const coursesDateFormatted = response.map(item => this.formatDateToDDMMYYYY(item));

    // Mark first course as unavailable (keeping original functionality)
    if (coursesDateFormatted.length > 0) {
      coursesDateFormatted[0].is_available = false;
    }

    return coursesDateFormatted.map(item => this.setRealCourseStatus(item));
  }

  getTagNames() {
    const coursesOverviewContainer = document.querySelector('.coursesaidashboard__structure');
    return {
      tagLive: coursesOverviewContainer.dataset.tagLive,
      tagDeployNeeded: coursesOverviewContainer.dataset.tagDeploymentNeeded,
      tagInProgress: coursesOverviewContainer.dataset.tagInProgress,
      tagGenerated: coursesOverviewContainer.dataset.tagGenerated
    };
  }

  /**
   * Set the real status of a course based on various properties
   * @param {Object} course - Course object
   * @returns {Object} Course with real status added
   */
  setRealCourseStatus = (course) => {
    if (!course) return course;
    const { tagLive, tagDeployNeeded, tagInProgress } = this.getTagNames();

    let realStatus;

    if (course.status === "live" && course.is_available && course.sanity_id && !course.sanity_id.includes("draft")) {
      realStatus = tagLive;
    } else if (course.status === "live" || (course.sanity_id && course.sanity_id.includes("draft")) && course.is_available === false) {
      realStatus = tagDeployNeeded.replaceAll(" ", "_");
    } else if (course.is_ai_generated && course.sanity_id && course.sanity_id.includes("draft") && course.is_available === false) {
      realStatus = tagInProgress;
    } else {
      realStatus = course.status || "";
    }

    course.realStatus = realStatus;

    return course;
  }

  /**
   * Format date to DD/MM/YYYY format
   * @param {Object} item - Item with created_at property
   * @returns {Object} Item with formatted date
   */
  formatDateToDDMMYYYY = (item) => {
    if (!item) return item;

    if (!item.created_at) {
      item.created_at = '';
      return item;
    }

    try {
      const date = new Date(item.created_at);
      if (isNaN(date.getTime())) {
        item.created_at = '';
        return item;
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      item.created_at = `${day}/${month}/${year}`;
    } catch (e) {
      item.created_at = '';
    }

    return item;
  }

  // FILTER SETUP METHODS

  /**
   * Set up course types for filtering
   */
  getCoursesTypesToFilter = () => {
    const hasAICourseGeneration = this.checkHasAICourseGeneration()
    this.coursesTypes = hasAICourseGeneration ? ["Your_own_courses", "AI_courses"] : ["Your_own_courses"]
  }

  /**
   * Extract unique course statuses for filtering
   */
  getCoursesStatusToFilter = () => {
    let coursesStatus = []
    if (this.allCourses && Array.isArray(this.allCourses)) {
      this.allCourses.forEach(course => {
        if (course && course.realStatus && 
            course.realStatus !== "concept" && 
            course.realStatus.trim() !== "") {
          coursesStatus = [...coursesStatus, course.realStatus.replace("and", "&")]
        }
      })
    }
    this.coursesStatus = new Set(coursesStatus)
    this.addStatusToFilterList()
  }

  // UI RENDERING METHODS

  /**
   * Create action squares for course creation
   * @returns {string} HTML for action squares
   */
  createDashedSquares = () => {
    const coursesOverviewContainer = document.querySelector('.coursesaidashboard__structure');
    const titleOfGenAi = coursesOverviewContainer.dataset.titleGenAi; 
    const titleOfCreateCourse = coursesOverviewContainer.dataset.titleCreateCourse;
    
    const hasAICourseGeneration = this.checkHasAICourseGeneration()
    const message = "Want to generate courses with our AI?"
    const messageLink = "Contact us"

    if(hasAICourseGeneration) {
      return (`                
        <a href="/coursesaigenerate" class="dashed-square square">
          <i class="fa-solid fa-circle-plus fa-xl"></i>
          <p>${titleOfGenAi}</p>
        </a>
        <a class="dashed-square square" href="${this.params.courseslink}" target="_blank">
          <i class="fa-solid fa-circle-plus fa-xl"></i>
          <p>${titleOfCreateCourse}</p>
        </a>`)
    } else {
      return (`
        <a href="https://collow.ai/contact/" target="_blank" class="dashed-square square disabled">
          <i class="fa-solid fa-circle-plus fa-xl" style="color: #707070"></i>
          <p>${message}</p><span>${messageLink}</span>
        </a>
        <a class="dashed-square square" href="${this.params.courseslink}" target="_blank">
          <i class="fa-solid fa-circle-plus fa-xl"></i>
          <p>${titleOfCreateCourse}</p>
        </a>`)
    }
  }

  /**
   * Create HTML for course cards
   * @param {Array} courses - List of courses to render
   * @returns {Array} HTML strings for each course
   */
  createCourseSquare = (courses) => {
    const { tagLive, tagDeployNeeded, tagInProgress, tagGenerated } = this.getTagNames();
    const status = {[tagLive]: "green", [tagGenerated]: "blue", [tagInProgress]: "orange", [tagDeployNeeded.replaceAll(" ", "_")]: "purple", test: "gray", concept: "gray"}

    return courses.map(course => {
      let tags = ''
      if (course.course_tags && Array.isArray(course.course_tags)) {
        course.course_tags.forEach(tag => tags += `<div class="course-tag">${tag}</div>`)
      }
      const realStatus = course.realStatus || '';
      return (
        ` <div class="course-square square">
            <div class="course-square__header">
                <div class="course-status ${status[realStatus] || ''}">${course.is_ai_generated && realStatus !== "" ? "✨ " : ""}${realStatus ? (realStatus.charAt(0).toUpperCase() + realStatus.slice(1)).replaceAll("_", " ").replace("and", "&") : ''}</div>
                ${course.is_available ? `<a href="course-introduction/${course.unique_identifier}"><i class="fa-solid fa-up-right-from-square fa-xs"></i></a>` : ""}
            </div>
            <div class="course-square__body">
                <div class="course-title">${course.course_name || ''}</div>
                <div class="course-generated-date">${"Generated on "}${course.created_at || ''}</div>
                <div class="course-tags-container">
                    <div class="course-tags">
                        ${tags}
                    </div>
                </div>
            </div>
            <div class="course-square__footer">
                <a href="${this.params.courseslink};${course.sanity_id || ''}" target="_blank">Bewerk training</a>
            </div>
          </div>`
      )
    })
  }

  /**
   * Render courses to the page
   * @param {Array} courses - List of courses to render
   */
  addCoursesToBody = (courses) => {
    const dashedSquares = this.createDashedSquares()
    const coursesWrapper = document.querySelector(".courses-wrapper")
    if (!coursesWrapper) return;

    coursesWrapper.innerHTML = ""
    coursesWrapper.innerHTML = dashedSquares

    if (courses && Array.isArray(courses)) {
      const courseElements = this.createCourseSquare(courses)
      courseElements.forEach(course => coursesWrapper.innerHTML += course)
    }
  }

  // FILTER UI METHODS

  /**
   * Set up event listeners for filters
   */
  addEventListener = () => {
    const typeList = document.querySelector(".type-list")
    const selectType = document.querySelector(".select-type")
    const statusList = document.querySelector(".status-list")
    const selectStatus = document.querySelector(".select-status")
    const checkboxes = document.querySelectorAll(".table-checkbox")

    if (selectType && typeList) {
      selectType.addEventListener("click", () => typeList.toggleAttribute("hidden"))
    }

    if (selectStatus && statusList) {
      selectStatus.addEventListener("click", () => statusList.toggleAttribute("hidden"))
    }

    if (checkboxes.length > 0) {
      checkboxes.forEach(checkbox => {
        if (checkbox) {
          checkbox.addEventListener("change", (e) => this.filterCourses(e))
        }
      })
    }
  }

  /**
   * Render type filter options
   */
  addTypesToFilterList = () => {
    const typesRef = {Eigen_trainingen: "own", AI_trainingen: "ai", Your_own_courses: "own", AI_courses: "ai"}
    const typesList = document.querySelector(".type-list")

    if (!typesList) return;

    typesList.innerHTML = '';

    if (this.coursesTypes && Array.isArray(this.coursesTypes)) {
      this.coursesTypes.forEach(type => {
        if (typeof type === 'string' && typesRef[type]) {
          typesList.innerHTML += `
            <li class="table-element">
                <input class="table-checkbox" type="checkbox" id="types" name="types" value=${typesRef[type]}> <label for=${typesRef[type]}>${type.replaceAll("_", " ")}</label>
            </li>`
        }
      })
    }
  }

  /**
   * Render status filter options
   */
  addStatusToFilterList = () => {
    const statusList = document.querySelector(".status-list")
    if (!statusList) return;

    statusList.innerHTML = '';

    if (this.coursesStatus && this.coursesStatus.size > 0) {
      this.coursesStatus.forEach(status => {
        if (typeof status === 'string') {
          const statusLower = status.toLowerCase();
          const statusCapitalized = status.charAt(0).toUpperCase() + status.slice(1).replaceAll("_", " ").replace("and", "&");
          statusList.innerHTML += `
            <li class="table-element">
                <input class="table-checkbox" type="checkbox" id="status" name="status" value=${statusLower}> <label for=${statusLower.replace("&", "and")}>${statusCapitalized}</label>
            </li>`;
        }
      })
    }
  }

  // FILTERING METHODS

  /**
   * Handle filter checkbox changes
   * @param {Event} e - Change event
   */
  filterCourses = (e) => {
    if (!e || !e.target) return;

    if(e.target.checked) {
      if(e.target.id === "status") {
        this.courseStatusFilters = [...this.courseStatusFilters, e.target.value]
      } else {
        this.courseTypesFilters = [...this.courseTypesFilters, e.target.value]
      }
    } else {
      this.courseStatusFilters = this.courseStatusFilters.filter(category => category !== e.target.value)
      this.courseTypesFilters = this.courseTypesFilters.filter(category => category !== e.target.value)
    }

    this.filterByTypes()
  }

  /**
   * Filter courses by status
   */
  filterByStatus = () => {
    let courseFilteredByStatus = []

    if (!this.courseStatusFilters || !Array.isArray(this.courseStatusFilters) || !this.coursesFiltered || !Array.isArray(this.coursesFiltered)) {
      return;
    }

    this.courseStatusFilters.forEach(type => {
      if (type) {
        const filteredCourses = this.coursesFiltered.filter(course => {
          return course && course.realStatus && course.realStatus.toLowerCase().replace("and", "&") === type;
        });
        courseFilteredByStatus = [...new Set([...courseFilteredByStatus, ...filteredCourses])];
      }
    });

    this.addCoursesToBody(courseFilteredByStatus);
  }

  /**
   * Filter courses by type
   */
  filterByTypes = () => {
    this.coursesFiltered = []

    if (!this.courseTypesFilters || !Array.isArray(this.courseTypesFilters) || this.courseTypesFilters.length === 0) {
      this.coursesFiltered = this.allCourses && Array.isArray(this.allCourses) ? [...this.allCourses] : [];
    } else if (this.allCourses && Array.isArray(this.allCourses)) {
      this.courseTypesFilters.forEach(type => {
        if (type) {
          const filteredCourses = this.allCourses.filter(course => {
            if (!course) return false;

            if(type === "ai") {
              return course.is_ai_generated === true;
            } else if(type === "own") {
              return course.is_ai_generated === false;
            } else {
              return true;
            }
          });

          this.coursesFiltered = [...new Set([...this.coursesFiltered, ...filteredCourses])];
        }
      });
    }

    this.addCoursesToBody(this.coursesFiltered);

    if (this.courseStatusFilters && Array.isArray(this.courseStatusFilters) && this.courseStatusFilters.length > 0) {
      this.filterByStatus();
    }
  }
};
